import React, { useState, useRef, useEffect } from 'react';
import styles from './SearchInput.module.scss';
const search = '/icons/searchSvg.svg';
import { useTranslation } from 'react-i18next';
import SearchItem from '../searchItem/SearchItem';
import DOMPurify from 'dompurify';
import useIsRtl from '../../../app/helpers/isRtl';
import getCookie from '../../../app/helpers/getCookie';

const SearchInputAnimated = ({
  isNavigation,
  data,
  className,
  searchDropdownClass,
}) => {
  const searchContainerRef = useRef(null);
  const isEnglish = getCookie('googtrans')?.includes('/ar/en');
  const { t } = useTranslation();
  const isRtl = useIsRtl();
  const [isActive, setIsActive] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchDropdown, setSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const inputRef = useRef(null);
  let filteredData = data?.reduce((acc, item) => {
    const itemTitleMatch = t(item?.title)
      ?.toLowerCase()
      ?.includes(inputValue.toLowerCase());

    const childrenTitleMatch = item?.children?.some((child) =>
      t(child?.title)?.toLowerCase()?.includes(inputValue.toLowerCase())
    );

    if (itemTitleMatch || childrenTitleMatch) {
      acc.push(item);
    }

    return acc;
  }, []);
  const clearInput = (e) => {
    e.stopPropagation();

    setInputValue('');
    setIsActive(true);
    setSearchDropdown(false);
    window.headerSearchIsOpen = false;
  };
  const handleMouseLeave = () => {
    if (!inputRef.current.contains(document.activeElement)) {
      setIsActive(false);
    }
  };

  const handleMouseEnter = () => {
    setIsActive(true);
  };
  const checkIfDropdownIsOpen = () => {
    setIsActive(true);
    setTimeout(() => {
      if (!isSearching) {
        setSearchDropdown(false);
        window.headerSearchIsOpen = false;
      }
    }, 300);
  };
  useEffect(() => {
    if (searchDropdown) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [searchDropdown]);

  useEffect(() => {
    const isTranslated = getCookie('googtrans');

    if (isTranslated && searchDropdown && searchContainerRef.current) {
      const searchItems =
        searchContainerRef.current.querySelectorAll('[data-search-item]');

      searchItems.forEach((item) => {
        const text = item.innerText.toLowerCase();
        const match = text.includes(inputValue.toLowerCase());
        item.style.display = match ? 'block' : 'none';
      });
    }
  }, [inputValue, searchDropdown]);

  const handleInputChange = (e) => {
    const sanitizedValue = DOMPurify.sanitize(e.target.value);
    setInputValue(sanitizedValue);
  };

  return (
    <>
      <div className={`${styles['input-container']} ${className ?? ''}`}>
        <div
          className={`${styles['input']} ${
            searchDropdown ? styles['with-dropdown'] : ''
          }`}
          data-active={isActive}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <input
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            placeholder={isEnglish ? 'Search' : ` ${t('common.search')}`}
            onFocus={() => setIsActive(true)}
            onBlur={checkIfDropdownIsOpen}
            onClick={() => setSearchDropdown(!searchDropdown)}
            style={{ textAlign: isRtl ? 'right' : '' }}
          />
          {isActive && (
            <div
              className={styles['clear-button']}
              onClick={clearInput}
              onMouseEnter={handleMouseEnter}
              onMouseDown={(e) => e.preventDefault()}
              style={{ left: isRtl ? '20px' : '' }}
            >
              ✕
            </div>
          )}
        </div>
        {!isActive && (
          <div
            className={styles['svg-container']}
            onMouseEnter={handleMouseEnter}
          >
            <img src={search} alt="Search Icon" />
          </div>
        )}

        {searchDropdown && isNavigation && (
          <div
            className={`${styles['search-dropdown']} ${searchDropdownClass ?? ''}`}
            onMouseEnter={() => (window.isHoveringCalendar = true)}
            onMouseLeave={() => (window.isHoveringCalendar = false)}
          >
            <div
              className={`${styles['dropdown']} ${isRtl ? 'rtl' : ''}`}
              ref={searchContainerRef}
            >
              {(getCookie('googtrans') ? data : filteredData)?.map(
                (val, index) => {
                  return (
                    <div key={index} data-search-item>
                      <SearchItem
                        itemData={val}
                        setIsSearching={setIsSearching}
                        closeNav={setSearchDropdown}
                      />
                    </div>
                  );
                }
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const SearchInput = ({
  inputClassname,
  onChange,
  disabled,
  isNavigation,
  value,
  data,
  hasClear,
  isMobile,
  color,
  onClear,
  textColor,
}) => {
  const { t } = useTranslation();
  const isRtl = useIsRtl();
  const isEnglish = getCookie('googtrans')?.includes('/ar/en');

  const [isActive, setIsActive] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [searchDropdown, setSearchDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const inputRef = useRef(null);
  const searchContainerRef = useRef(null);

  useEffect(() => {
    if (typeof value === 'string') setInputValue(value);
  }, [value]);

  const reducedFilter = (list, needle) =>
    list?.reduce((acc, item) => {
      const title = t(item?.title)?.toLowerCase() || '';
      const matchSelf = title.includes(needle);

      const matchChildren = item?.children?.some((child) =>
        (t(child?.title)?.toLowerCase() || '').includes(needle)
      );

      if (matchSelf || matchChildren) acc.push(item);
      return acc;
    }, []);

  const isTranslated = !!getCookie('googtrans');
  const needle = inputValue.toLowerCase();
  const filteredData = isTranslated ? data : reducedFilter(data, needle);
  const listData = filteredData;

  useEffect(() => {
    if (!isTranslated || !searchDropdown || !searchContainerRef.current) return;
    const items =
      searchContainerRef.current.querySelectorAll('[data-search-item]');
    const q = needle;
    items.forEach((el) => {
      const text = el.innerText.toLowerCase();
      el.style.display = text.includes(q) ? 'block' : 'none';
    });
  }, [isTranslated, searchDropdown, needle]);

  const clearInput = (e) => {
    e?.stopPropagation?.();
    setInputValue('');
    setIsActive(true);
    setSearchDropdown(false);
    setIsSearching(false);
    onClear?.('');
  };

  const handleMouseLeave = () => {
    if (!inputRef.current?.contains(document.activeElement)) {
      setIsActive(false);
    }
  };

  const handleMouseEnter = () => setIsActive(true);

  const handleInputChange = (e) => {
    const sanitizedValue = DOMPurify.sanitize(e.target.value);
    setInputValue(sanitizedValue);
    onChange?.(e);
  };

  return (
    <div
      className={`${styles['unanimated-search']} ${inputClassname ?? ''} ${isRtl ? 'rtl' : ''}`}
      onMouseEnter={() => (window.isHoveringCalendar = true)}
      onMouseLeave={() => (window.isHoveringCalendar = false)}
    >
      <div
        className={styles['input']}
        data-active={isActive}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <input
          ref={inputRef}
          value={inputValue}
          disabled={disabled}
          onChange={handleInputChange}
          placeholder={isEnglish ? 'Search' : `${t('common.search')}`}
          onFocus={() => setIsActive(true)}
          onClick={() => setSearchDropdown((s) => !s)}
          style={{ textAlign: isRtl ? 'right' : '' }}
        />

        {isActive && hasClear && (
          <div
            className={
              isRtl ? styles['clear-button-rtl'] : styles['clear-button']
            }
            onClick={clearInput}
            onMouseEnter={handleMouseEnter}
            onMouseDown={(e) => e.preventDefault()}
          >
            <span style={{ color: color || 'green' }}>X</span>
          </div>
        )}
      </div>

      {((isNavigation && searchDropdown) || isMobile) && (
        <div className={styles['search-dropdown-mobile']}>
          <div
            className={`${styles['dropdown']} ${isRtl ? 'rtl' : ''}`}
            ref={searchContainerRef}
          >
            {listData?.map((val, index) => (
              <div key={index} data-search-item>
                <SearchItem
                  key={index}
                  itemData={val}
                  setIsSearching={setIsSearching}
                  isMobile={isMobile}
                  closeNav={setSearchDropdown}
                  textColor={textColor}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export { SearchInputAnimated, SearchInput };
